import { Button } from '@territory/ui';
import { WEDGE_NOTICE as N, WEDGE_SHELL as B } from '@territory/tokens';
import { CSSProperties, useEffect, useMemo, useState } from 'react';

/**
 * /share/text and /share/email — bridge routes for the cold-email share buttons.
 *
 * Gmail iOS's link rewriter only whitelists http/https, so inline <a href="sms:...">
 * or <a href="mailto:..."> can get stripped before the OS sees them. The fix: link
 * the email button to a hosted https route that performs the sms:/mailto: navigation
 * via window.location.href once the page loads. iOS picks up the scheme from the
 * navigation and opens Messages/Mail pre-filled.
 *
 * Same pattern as /copy. Mounted outside WedgeAuthGate so cold taps from email
 * don't bounce off the auth wall.
 */

type Via = 'text' | 'email';
type Status = 'redirecting' | 'manual' | 'missing-invite';

const SMS_BODY = (inviteUrl: string) =>
  `Try PREP. Walk into any account sharp in 30 seconds. Use my code: ${inviteUrl}`;

const EMAIL_SUBJECT = 'You should try PREP';
const EMAIL_BODY = (invite: string, inviteUrl: string) =>
  `Try PREP. Walk into any account sharp in 30 seconds. Use my code ${invite} to get 2 extra briefs: ${inviteUrl}`;

export function ShareRedirect({ via }: { via: Via }) {
  const { invite, inviteUrl } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('invite') || '').trim();
    return {
      invite: code,
      inviteUrl: code
        ? `https://wedge.repvantage.io/?invite=${encodeURIComponent(code)}`
        : 'https://wedge.repvantage.io/',
    };
  }, []);

  const shareUrl = useMemo(() => {
    if (!invite) return '';
    if (via === 'text') {
      return `sms:?body=${encodeURIComponent(SMS_BODY(inviteUrl))}`;
    }
    return `mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY(invite, inviteUrl))}`;
  }, [via, invite, inviteUrl]);

  const [status, setStatus] = useState<Status>('redirecting');

  useEffect(() => {
    if (!invite) {
      setStatus('missing-invite');
      return;
    }

    let cancelled = false;

    // Fire the navigation. iOS / Android intercepts the scheme and opens the app.
    window.location.href = shareUrl;

    // If we're still on the page after ~1.6s, the scheme didn't fire (desktop
    // browser, scheme blocked, etc.). Show manual fallback so the rep isn't stuck.
    const t = window.setTimeout(() => {
      if (!cancelled) setStatus('manual');
    }, 1600);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [shareUrl, invite]);

  const channelLabel = via === 'text' ? 'iMessage' : 'your email app';
  const channelVerb = via === 'text' ? 'Text' : 'Email';

  const status_title =
    status === 'missing-invite'
      ? 'Missing your invite code.'
      : status === 'manual'
        ? `${channelLabel} didn’t open.`
        : `Opening ${channelLabel}…`;

  const status_sub =
    status === 'missing-invite'
      ? 'The share link is malformed. Head back to your PREP email and re-tap.'
      : status === 'manual'
        ? 'Copy the message below and send it yourself.'
        : 'Pre-filled with your code and a link your friend can tap.';

  const manualMessage =
    via === 'text' ? SMS_BODY(inviteUrl) : `${EMAIL_SUBJECT}\n\n${EMAIL_BODY(invite, inviteUrl)}`;

  // Styles (matches wedge dark theme, same aesthetic as CopyCode.tsx)
  const wrap: CSSProperties = {
    minHeight: '100vh',
    background: B.bg,
    color: B.text,
    fontFamily:
      N.shareFont,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box',
  };

  const card: CSSProperties = {
    width: '100%',
    maxWidth: 440,
    background: N.shareCard,
    border: N.shareCardBorder,
    borderRadius: 16,
    padding: '44px 32px 36px',
    textAlign: 'center',
    boxShadow: N.shareCardShadow,
  };

  const wordmark: CSSProperties = {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.4,
    marginBottom: 8,
    color: N.wordmark,
    lineHeight: 1,
  };

  const tagline: CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: N.shareTagline,
    marginBottom: 32,
  };

  const statusStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.2,
    lineHeight: 1.3,
    margin: '0 0 8px',
    color: B.text,
  };

  const subStyle: CSSProperties = {
    fontSize: 13,
    lineHeight: 1.55,
    color: N.shareSubtext,
    margin: '0 0 24px',
  };

  const manualBox: CSSProperties = {
    display: status === 'manual' ? 'block' : 'none',
    background: N.shareManual,
    border: N.shareManualBorder,
    borderRadius: 8,
    padding: '14px 16px',
    fontFamily: N.shareMono,
    fontSize: 12.5,
    lineHeight: 1.55,
    color: B.amber,
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: 'all',
    marginBottom: 20,
  };

  const footer: CSSProperties = {
    marginTop: 22,
    paddingTop: 18,
    borderTop: N.shareFooterBorder,
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    fontFamily: N.shareFooterMono,
    color: N.shareFooterText,
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={wordmark}>
          Rep<span style={{ color: B.amber }}>Vantage</span>
        </div>
        <div style={tagline}>Sales Intelligence Platform</div>

        <p style={statusStyle}>{status_title}</p>
        <p style={subStyle}>{status_sub}</p>

        <div style={manualBox}>{manualMessage}</div>

        <Button appearance="wedge-redirect" redirectLook="share-retry" href={shareUrl || 'https://wedge.repvantage.io/'}>
          Try {channelVerb} again →
        </Button>

        <div style={footer}>© 2026 RepVantage LLC · Made for the field</div>
      </div>
    </div>
  );
}
